import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  CreativeReportRow,
  CreativeReportColumn,
  Creative,
  AssignedLabelsResponse,
  AssignedLabelItem,
  CreativeReportAd,
  VideoRetentionData,
} from "../../types";
import { useGlobalStore } from "@/GlobalStore";

export type CreativeWithRetention = Creative & {
  type: "image" | "video" | "mixed";
  ad_name: string;
  ad_id?: string;
  creative_id?: string;
  videoRetentionData?: VideoRetentionData;
} & Record<string, unknown>;
import CardHeader from "./CardHeader";
import CardMetrics from "./CardMetrics";
import AdCard from "@/components/common/adCard";
import OptionsAction from "@/components/common/adCard/actions/OptionsAction";
import AskAIAction from "@/components/common/adCard/actions/AskAIAction";
import { updateAssignedLabel } from "../../apiService";
import ToastContent from "@/components/common/toast/ToastContent";
import { toast } from "sonner";
import { LuCircleCheck } from "react-icons/lu";
import { useAttachedCreativesStore } from "../../store/attachedCreativesStore";

interface CreativeCardProps {
  data: CreativeReportRow | CreativeReportRow["ads"][0];
  columns: CreativeReportColumn[];
  ad_columns?: CreativeReportColumn[];
  onShopNowClick?: () => void;
  onPanelClick?: () => void;
  showCardHeader?: boolean;
  isAdView?: boolean;
  groupType?: string;
  groupByKey?: string;
  creativeType?: string;
  labelData?: AssignedLabelsResponse | null;
  setLabelData?: React.Dispatch<React.SetStateAction<AssignedLabelsResponse | null>>;
  labelView?: string;
  labelConfigId?: string;
  uniqueLabels?: string[];
  accountId?: string;
  onGalleryOpen?: () => void;
  onGalleryClose?: () => void;
  selectedDateView?: string;
  selectedGalleryItems?:
    | (Creative & {
        type: "image" | "video" | "mixed";
        ad_name: string;
        ad_id?: string;
        creative_id?: string;
      } & Record<string, unknown>)[]
    | null;
  removeCard: (colIndex: number, rowIndex: number) => void;
  colIndex: number;
  rowIndex: number;
  sectionId?: string;
  sectionMetricLabels?: { id: string; label: string }[];
  forcedAspectRatio?: string;
  containerWidth?: number;
  fetchGalleryAds?: (
    groupValue: string,
    page: number,
    pageSize: number,
  ) => Promise<import("../../types").AdDetailResponse | null>;
}

const CreativeCard = ({
  data,
  columns,
  ad_columns,
  onPanelClick,
  showCardHeader = true,
  isAdView = false,
  groupType,
  groupByKey,
  creativeType,
  labelData,
  setLabelData,
  labelView,
  labelConfigId,
  uniqueLabels,
  accountId,
  onGalleryOpen,
  onGalleryClose,
  selectedDateView = "compare",
  selectedGalleryItems,
  removeCard,
  colIndex,
  rowIndex,
  sectionId,
  sectionMetricLabels,
  forcedAspectRatio,
  containerWidth,
  fetchGalleryAds,
}: CreativeCardProps) => {
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [isLoadingNextBatch, setIsLoadingNextBatch] = useState(false);
  const [isGalleryFetching, setIsGalleryFetching] = useState(false);
  const [cardGalleryOpen, setCardGalleryOpen] = useState(false);
  const nextBatchPageRef = useRef(1);

  // Attached creatives store
  const { isCreativeAttached, attachCreativeAndOpenChat, detachAndMaybeCloseChat } =
    useAttachedCreativesStore();

  // Initialize adCreatives immediately from props (without retention data for fast render)
  const [adCreatives, setAdsCreatives] = useState<CreativeWithRetention[]>(() => {
    if (isAdView) {
      const ad = data as CreativeReportAd;

      // Extract winning criteria values from ad
      const winningCriteriaData: Record<string, boolean> = {};
      if (ad_columns) {
        const winningColumns = ad_columns.filter(
          (col) =>
            (col as CreativeReportColumn & { winning_criteria_name?: string })
              .winning_criteria_name,
        );
        winningColumns.forEach((col) => {
          const value = (ad as Record<string, unknown>)[col.key];
          if (value === true) {
            winningCriteriaData[col.key] = true;
          }
        });
      }

      return [
        {
          ...ad.creative,
          type: ad.creative_type,
          ad_name: ad.ad_name || "",
          ad_id: ad.ad_id || "",
          creative_id: ad.creative_id || "",
          videoRetentionData: undefined, // Will be computed in background
          ...winningCriteriaData, // Include winning criteria fields
        },
      ];
    } else {
      const ads = data.ads as CreativeReportAd[];
      if (ads.length > 0) {
        return ads.map((ad) => {
          const winningCriteriaData: Record<string, boolean> = {};
          if (ad_columns) {
            const winningColumns = ad_columns.filter(
              (col) =>
                (col as CreativeReportColumn & { winning_criteria_name?: string })
                  .winning_criteria_name,
            );
            winningColumns.forEach((col) => {
              const value = (ad as Record<string, unknown>)[col.key];
              if (value === true) {
                winningCriteriaData[col.key] = true;
              }
            });
          }

          return {
            ...ad.creative,
            type: ad.creative_type,
            ad_name: ad.ad_name || "",
            ad_id: ad.ad_id || "",
            creative_id: ad.creative_id || "",
            videoRetentionData: undefined,
            ...winningCriteriaData,
          };
        });
      }
      const row = data as CreativeReportRow;
      // v2: use first_ad_metadata for initial display when ads is empty
      const firstAdMeta = row.first_ad_metadata;
      if (firstAdMeta?.creative) {
        return [
          {
            ...firstAdMeta.creative,
            type: firstAdMeta.creative_type,
            ad_name: firstAdMeta.ad_name || "",
            ad_id: firstAdMeta.ad_id || "",
            creative_id: firstAdMeta.creative_id || "",
            videoRetentionData: undefined,
          },
        ];
      }
      if (row.creative_thumbnail) {
        return [
          {
            images: [
              { url: row.creative_thumbnail, permalink_url: row.creative_thumbnail },
            ],
            videos: [],
            thumbnail_url: row.creative_thumbnail,
            type: row.creative_type,
            ad_name: "",
            ad_id: "",
            creative_id: "",
            videoRetentionData: undefined,
          } as unknown as CreativeWithRetention,
        ];
      }
      return [];
    }
  });

  // Helper to compute retention data for a single ad
  const computeRetentionData = useCallback(
    (ad: CreativeReportAd): VideoRetentionData | undefined => {
      if (!ad_columns) return undefined;

      const retentionColumns = ad_columns.filter(
        (col) => col.column_type === "media_data_node" && col.percentage !== undefined,
      );

      if (retentionColumns.length === 0) return undefined;

      const retentionData: VideoRetentionData = [];

      retentionColumns.forEach((col) => {
        const value = ad[col.key];
        if (typeof value === "number" && !isNaN(value)) {
          retentionData.push({
            percentage: col.percentage!,
            retention: value,
          });
        } else if (typeof value === "string" && value !== "" && value !== "N/A") {
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            retentionData.push({
              percentage: col.percentage!,
              retention: numValue,
            });
          }
        }
      });

      return retentionData.length > 0
        ? retentionData.sort((a, b) => a.percentage - b.percentage)
        : undefined;
    },
    [ad_columns],
  );

  // Update creatives when data changes (without retention data for fast render)
  useEffect(() => {
    if (isAdView) {
      const ad = data as CreativeReportAd;

      // Extract winning criteria values from ad
      const winningCriteriaData: Record<string, boolean> = {};
      if (ad_columns) {
        const winningColumns = ad_columns.filter(
          (col) =>
            (col as CreativeReportColumn & { winning_criteria_name?: string })
              .winning_criteria_name,
        );
        winningColumns.forEach((col) => {
          const value = (ad as Record<string, unknown>)[col.key];
          if (value === true) {
            winningCriteriaData[col.key] = true;
          }
        });
      }

      const creativeObj: CreativeWithRetention = {
        ...ad.creative,
        type: ad.creative_type,
        ad_name: ad.ad_name || "",
        ad_id: ad.ad_id || "",
        creative_id: ad.creative_id || "",
        videoRetentionData: undefined, // Will be computed in background
        ...winningCriteriaData, // Include winning criteria fields
      };
      setAdsCreatives([creativeObj]);
    } else {
      const ads = data.ads as CreativeReportAd[];
      if (ads.length > 0) {
        const _adsCreatives: CreativeWithRetention[] = ads.map((ad) => {
          const winningCriteriaData: Record<string, boolean> = {};
          if (ad_columns) {
            const winningColumns = ad_columns.filter(
              (col) =>
                (col as CreativeReportColumn & { winning_criteria_name?: string })
                  .winning_criteria_name,
            );
            winningColumns.forEach((col) => {
              const value = (ad as Record<string, unknown>)[col.key];
              if (value === true) {
                winningCriteriaData[col.key] = true;
              }
            });
          }

          return {
            ...ad.creative,
            type: ad.creative_type,
            ad_name: ad.ad_name || "",
            ad_id: ad.ad_id || "",
            creative_id: ad.creative_id || "",
            videoRetentionData: undefined,
            ...winningCriteriaData,
          };
        });
        setAdsCreatives(_adsCreatives);
      } else {
        const row = data as CreativeReportRow;
        const firstAdMeta = row.first_ad_metadata;
        if (firstAdMeta?.creative) {
          setAdsCreatives([
            {
              ...firstAdMeta.creative,
              type: firstAdMeta.creative_type,
              ad_name: firstAdMeta.ad_name || "",
              ad_id: firstAdMeta.ad_id || "",
              creative_id: firstAdMeta.creative_id || "",
              videoRetentionData: undefined,
            },
          ]);
        } else if (row.creative_thumbnail) {
          setAdsCreatives([
            {
              images: [
                { url: row.creative_thumbnail, permalink_url: row.creative_thumbnail },
              ],
              videos: [],
              thumbnail_url: row.creative_thumbnail,
              type: row.creative_type,
              ad_name: "",
              ad_id: "",
              creative_id: "",
              videoRetentionData: undefined,
            } as unknown as CreativeWithRetention,
          ]);
        } else {
          setAdsCreatives([]);
        }
      }
    }
  }, [data, isAdView, ad_columns]);

  // Compute retention data in background after initial render
  useEffect(() => {
    // Use requestIdleCallback for background computation, fallback to setTimeout
    const scheduleUpdate = (callback: () => void) => {
      if (typeof requestIdleCallback !== "undefined") {
        requestIdleCallback(callback, { timeout: 100 });
      } else {
        setTimeout(callback, 0);
      }
    };

    scheduleUpdate(() => {
      setAdsCreatives((prevCreatives) => {
        if (isAdView) {
          const ad = data as CreativeReportAd;
          const currentCreative = prevCreatives[0];
          // Skip if already computed for this ad
          if (
            currentCreative?.ad_id === ad.ad_id &&
            (ad.creative_type !== "video" ||
              currentCreative.videoRetentionData !== undefined)
          ) {
            return prevCreatives;
          }
          return prevCreatives.map((creative) => {
            if (creative.ad_id === ad.ad_id && ad.creative_type === "video") {
              return {
                ...creative,
                videoRetentionData: computeRetentionData(ad),
              };
            }
            return creative;
          });
        } else {
          const ads = data.ads as CreativeReportAd[];
          // Check if all video creatives already have retention data computed
          const needsUpdate = prevCreatives.some((creative, idx) => {
            const ad = ads[idx];
            return (
              ad &&
              ad.creative_type === "video" &&
              creative.ad_id === ad.ad_id &&
              creative.videoRetentionData === undefined
            );
          });
          if (!needsUpdate) {
            return prevCreatives;
          }
          return prevCreatives.map((creative, idx) => {
            const ad = ads[idx];
            if (ad && creative.ad_id === ad.ad_id && ad.creative_type === "video") {
              return {
                ...creative,
                videoRetentionData: computeRetentionData(ad),
              };
            }
            return creative;
          });
        }
      });
    });
  }, [data, isAdView, computeRetentionData]);

  // Extract video retention data for the current ad
  const currentVideoRetentionData = useMemo((): VideoRetentionData | undefined => {
    if (!ad_columns) return undefined;

    // Find media_data_node columns (retention percentages)
    const retentionColumns = ad_columns.filter(
      (col) => col.column_type === "media_data_node" && col.percentage !== undefined,
    );

    if (retentionColumns.length === 0) return undefined;

    // Get the current ad data
    const currentAd = isAdView
      ? (data as CreativeReportAd)
      : (data.ads as CreativeReportAd[])?.[currentAdIndex];

    if (!currentAd) return undefined;

    // Extract retention values from the ad data
    const retentionData: VideoRetentionData = [];

    retentionColumns.forEach((col) => {
      const value = currentAd[col.key];
      // Only add valid numeric values
      if (typeof value === "number" && !isNaN(value)) {
        retentionData.push({
          percentage: col.percentage!,
          retention: value,
        });
      } else if (typeof value === "string" && value !== "" && value !== "N/A") {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          retentionData.push({
            percentage: col.percentage!,
            retention: numValue,
          });
        }
      }
    });

    // Sort by percentage and return only if we have valid data
    return retentionData.length > 0
      ? retentionData.sort((a, b) => a.percentage - b.percentage)
      : undefined;
  }, [ad_columns, data, isAdView, currentAdIndex]);

  // Filter and format metrics based on view type
  const formattedMetrics = columns
    .filter(
      (col, index) =>
        (col.column_type === "number" || col.column_type === "text") && index !== 0,
    )
    .sort((a, b) => {
      if (a.column_type === "text" && b.column_type !== "text") return -1;
      if (a.column_type !== "text" && b.column_type === "text") return 1;
      return 0;
    })
    .map((column) => {
      const value = isAdView
        ? (data as CreativeReportRow["ads"][0])[column.key]
        : (data as CreativeReportRow)[column.key];
      const numValue = Number(value);
      const formattedValue = !isNaN(numValue)
        ? `${column.prefix || ""}${numValue.toLocaleString()}${column.suffix || ""}`
        : String(value);

      // Try to get the percent change value from the data, using the convention: `${column.key}_change`
      const percentChangeKey = `${column.key}_percent_change`;
      const percentChangeValue = isAdView
        ? (data as CreativeReportRow["ads"][0])[percentChangeKey]
        : (data as CreativeReportRow)[percentChangeKey];

      let percentChange: string | null = null;
      // Only show percentage changes when selectedDateView is "compare"
      if (selectedDateView === "compare") {
        if (typeof percentChangeValue === "number" && !isNaN(percentChangeValue)) {
          // Format as +X.X% or -X.X%
          percentChange = `${percentChangeValue > 0 ? "+" : ""}${percentChangeValue.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
        } else if (
          typeof percentChangeValue === "string" &&
          percentChangeValue.trim() !== ""
        ) {
          percentChange = percentChangeValue === "N/A" ? "-" : percentChangeValue;
        }
      }

      return {
        key: column.key,
        label: column.label,
        value: formattedValue,
        percentChange,
        warning: column?.messages?.warning,
        external: column?.sub_type === "EXTERNAL",
      };
    });

  const handlePrevious = () => {
    setCurrentAdIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const totalAdsCount = !isAdView
    ? Number((data as CreativeReportRow).total_ads ?? 0)
    : 0;

  const handleNext = useCallback(() => {
    const isAtLastLoaded = currentAdIndex >= adCreatives.length - 1;
    const hasMore = totalAdsCount > adCreatives.length;

    if (
      isAtLastLoaded &&
      hasMore &&
      fetchGalleryAds &&
      !isLoadingNextBatch &&
      !isAdView &&
      groupByKey
    ) {
      const raw = (data as Record<string, unknown>)[groupByKey];
      const groupValue =
        typeof raw === "string" || typeof raw === "number" ? String(raw) : "";
      if (!groupValue) return;

      setIsLoadingNextBatch(true);
      const page = nextBatchPageRef.current;
      fetchGalleryAds(groupValue, page, 10)
        .then((response) => {
          if (response?.status === "success") {
            nextBatchPageRef.current = page + 1;
            if (response.ads.length > 0) {
              const newCreatives: CreativeWithRetention[] = response.ads.map((ad) => ({
                ...ad.creative,
                type: ad.creative_type,
                ad_name: ad.ad_name || "",
                ad_id: ad.ad_id || "",
                creative_id: ad.creative_id || "",
                videoRetentionData: undefined,
              }));
              setAdsCreatives((prev) => {
                const existingIds = new Set(prev.map((c) => c.ad_id).filter(Boolean));
                const dedupedNew = newCreatives.filter(
                  (c) => !c.ad_id || !existingIds.has(c.ad_id),
                );
                return [...prev, ...dedupedNew];
              });
              setCurrentAdIndex((prev) => prev + 1);
            }
          }
        })
        .catch(() => {})
        .finally(() => {
          setIsLoadingNextBatch(false);
        });
    } else if (!isAtLastLoaded) {
      setCurrentAdIndex((prev) => prev + 1);
    }
  }, [
    currentAdIndex,
    adCreatives.length,
    totalAdsCount,
    fetchGalleryAds,
    isLoadingNextBatch,
    isAdView,
    groupByKey,
    data,
  ]);

  const currentGroup = data[
    columns.find((col) => col.is_primary)?.key ?? columns[0]?.key
  ] as string;

  const getCurrentLabelItems = (adId: string) => {
    if (!labelData?.assigned_labels) return [] as AssignedLabelItem[];
    const entry = labelData.assigned_labels.find((l) => l.ad_id === adId);
    if (!entry) return [] as AssignedLabelItem[];
    return entry.labels;
  };

  // Get selected account for winning criteria
  const selectedAccount = useGlobalStore((state) => state.selectedAccount);

  // Get winning criteria data for the current ad
  const winningCriteria = useMemo(() => {
    const winningFilters = selectedAccount?.additional_info?.winning_filters;
    if (!winningFilters || !ad_columns) return [];

    // Get current ad data
    const currentAd = isAdView
      ? (data as CreativeReportAd)
      : (data as CreativeReportRow).ads[currentAdIndex];

    if (!currentAd) return [];

    // Find winning criteria columns
    const winningColumns = ad_columns.filter(
      (col) =>
        (col as CreativeReportColumn & { winning_criteria_name?: string })
          .winning_criteria_name,
    );

    // Get winning criteria data
    const winningCriteriaData = winningColumns
      .filter((col) => {
        const adValue = (currentAd as Record<string, unknown>)[col.key];
        return adValue === true;
      })
      .map((col) => {
        const winningCriteriaName = (
          col as CreativeReportColumn & {
            winning_criteria_name?: string;
          }
        ).winning_criteria_name;
        if (!winningCriteriaName) return null;

        // Find matching criteria in winning_filters
        const criteria = winningFilters
          .flatMap((section) => section.criterias)
          .find((c) => c.criteria_name === winningCriteriaName);

        const colorCode = (
          criteria as { criteria_name: string; color_code?: string } | undefined
        )?.color_code;

        return {
          criteriaName: winningCriteriaName,
          colorCode,
        };
      })
      .filter(
        (item): item is { criteriaName: string; colorCode: string } =>
          item !== null && Boolean(item.colorCode),
      );

    return winningCriteriaData;
  }, [selectedAccount, data, isAdView, currentAdIndex, ad_columns]);

  const handleLabelsUpdate = async (adId: string, labels: AssignedLabelItem[]) => {
    if (!accountId || !labelConfigId || groupType !== "text_ai_label") return;
    try {
      const targetCreative = adCreatives.find((creative) => creative.ad_id === adId);
      if (!targetCreative) return;

      const response = await updateAssignedLabel(accountId, labelConfigId, {
        ad_id: adId,
        labels,
      });

      setLabelData?.((prev) =>
        prev ? { ...prev, assigned_labels: response.assigned_labels } : null,
      );

      // If the new labels do not include the current group, remove the creative
      if (currentGroup && !labels.some((l) => l.label === currentGroup)) {
        const updatedCreatives = adCreatives.filter(
          (creative) => creative.creative_id !== targetCreative.creative_id,
        );
        // if length is  0 remove group
        if (updatedCreatives.length === 0) removeCard(colIndex, rowIndex);
        else setAdsCreatives(updatedCreatives);
        // resetting current index for the last ad group change
        if (currentAdIndex > 0 && currentAdIndex > updatedCreatives.length - 1)
          setCurrentAdIndex((i) => i - 1);
      }

      // Additionally, for video sequence edits (any labelView set), remove the creative after save
      if (labelView === "sequence" && targetCreative.type === "video") {
        const updatedCreatives = adCreatives.filter(
          (creative) => creative.creative_id !== targetCreative.creative_id,
        );
        // if length is  0 remove group
        if (updatedCreatives.length === 0) removeCard(colIndex, rowIndex);
        else setAdsCreatives(updatedCreatives);
        // resetting current index for the last ad group change
        if (currentAdIndex > 0 && currentAdIndex > updatedCreatives.length - 1)
          setCurrentAdIndex((i) => i - 1);
      }

      // Emit custom event with the specific labelConfigId
      const event = new CustomEvent("labelUpdated", {
        detail: { labelConfigId },
      });
      window.dispatchEvent(event);

      toast.message(
        <ToastContent
          icon={LuCircleCheck}
          headingPrefix={`Labels updated`}
          subheadingPrefix="Ad labels updated successfully."
        />,
      );
    } catch (error) {
      console.error("Error updating labels:", error);
      toast.message(
        <ToastContent
          headingPrefix={`Error updating labels`}
          subheadingPrefix="Failed to update labels. Please try again."
          isError={true}
        />,
      );
    }
  };

  // v2 gallery infinite scroll for CardHeader
  const galleryNextPageRef = useRef(1);
  const galleryTotalCreatives = useMemo(() => {
    if (!fetchGalleryAds || isAdView) return undefined;
    const totalAds = (data as CreativeReportRow).total_ads;
    return totalAds != null ? Number(totalAds) : undefined;
  }, [fetchGalleryAds, isAdView, data]);

  const handleCardGalleryLoadMore = useCallback(async () => {
    if (!fetchGalleryAds || isAdView || !groupByKey) return;
    const raw = (data as Record<string, unknown>)[groupByKey];
    const groupValue =
      typeof raw === "string" || typeof raw === "number" ? String(raw) : "";
    if (!groupValue) return;
    const page = galleryNextPageRef.current;
    const response = await fetchGalleryAds(groupValue, page, 25);
    if (!response || response.status !== "success" || !response.ads.length) return;
    const newCreatives: CreativeWithRetention[] = response.ads.map((ad) => ({
      ...ad.creative,
      type: ad.creative_type,
      ad_name: ad.ad_name || "",
      ad_id: ad.ad_id || "",
      creative_id: ad.creative_id || "",
      videoRetentionData: undefined,
    }));
    setAdsCreatives((prev) => {
      const existingIds = new Set(prev.map((c) => c.ad_id).filter(Boolean));
      const dedupedNew = newCreatives.filter(
        (c) => !c.ad_id || !existingIds.has(c.ad_id),
      );
      return [...prev, ...dedupedNew];
    });
    galleryNextPageRef.current = page + 1;
  }, [fetchGalleryAds, isAdView, groupByKey, data]);

  return (
    <div className="group/card w-full h-full flex flex-col p-2 rounded-[12px] squircle squircle-radius-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white shadow-sm">
      {showCardHeader && (
        <CardHeader
          title={currentGroup}
          currentAdIndex={currentAdIndex}
          totalAds={
            !isAdView && (data as CreativeReportRow).total_ads != null
              ? Number((data as CreativeReportRow).total_ads)
              : adCreatives.length
          }
          mediaItems={selectedGalleryItems || adCreatives}
          onPrevious={handlePrevious}
          isAdsLoading={isLoadingNextBatch}
          onNext={handleNext}
          onPanelClick={onPanelClick}
          mediaType={
            (isAdView
              ? (data as CreativeReportRow["ads"][0]).creative_type
              : (data as CreativeReportRow).creative_type) || "image"
          }
          groupType={groupType}
          labelConfigId={labelConfigId}
          accountId={accountId}
          {...(fetchGalleryAds
            ? {
                galleryOpen: cardGalleryOpen,
                setGalleryOpen: (open: boolean) => {
                  setCardGalleryOpen(open);
                  if (!open) {
                    setIsGalleryFetching(false);
                    onGalleryClose?.();
                  }
                },
                onGalleryOpen: () => {
                  setCardGalleryOpen(true);
                  onGalleryOpen?.();
                  if (Number((data as Record<string, unknown>).total_ads ?? 0) <= 1)
                    return;
                  setIsGalleryFetching(true);
                  galleryNextPageRef.current = 1;
                  handleCardGalleryLoadMore().then(() => {
                    setIsGalleryFetching(false);
                  });
                },
                onGalleryClose: () => {
                  setCardGalleryOpen(false);
                  onGalleryClose?.();
                },
                isGalleryLoading: isGalleryFetching,
              }
            : {
                onGalleryOpen,
                onGalleryClose,
              })}
          creativeType={creativeType}
          labelView={labelView}
          currentGroup={currentGroup}
          uniqueLabels={uniqueLabels}
          sequenceData={(() => {
            const primaryKey = columns.find((c) => c.is_primary)?.key ?? columns[0]?.key;
            const sequenceKey = `${primaryKey}_sequences` as keyof typeof data;
            const maybeArray = (data as Record<string, unknown>)[sequenceKey as string];
            return Array.isArray(maybeArray)
              ? (maybeArray as Array<{ label: string; order: number }>)
              : undefined;
          })()}
          ad_columns={ad_columns}
          galleryTotalCreatives={galleryTotalCreatives}
          onGalleryLoadMore={fetchGalleryAds ? handleCardGalleryLoadMore : undefined}
        />
      )}
      <AdCard
        winningCriteria={isAdView ? undefined : winningCriteria}
        body={{
          media: {
            ad_id: adCreatives[currentAdIndex]?.ad_id || "",
            source: adCreatives[currentAdIndex]?.videos?.[0]
              ? adCreatives[currentAdIndex]?.videos?.[0]?.permalink_url
              : adCreatives[currentAdIndex]?.images?.[0]?.permalink_url,
            type: adCreatives[currentAdIndex]?.videos?.[0] ? "video" : "image",
            thumbnail: adCreatives[currentAdIndex]?.thumbnail_url,
            videoRetentionData: adCreatives[currentAdIndex]?.videos?.[0]
              ? currentVideoRetentionData
              : undefined,
            forcedAspectRatio: forcedAspectRatio,
            containerWidth: containerWidth ? containerWidth - 18 : undefined,
            actions: [
              // Only show Ask AI action when groupByKey is "creative_name"
              ...(groupByKey === "creative_name"
                ? (() => {
                    const creativeId = adCreatives[currentAdIndex]?.creative_id || "";
                    const isAttached = isCreativeAttached(creativeId, sectionId || "");
                    const visibility: "always" | "hover" = isAttached
                      ? "always"
                      : "hover";
                    return [
                      {
                        component: (
                          <AskAIAction
                            isAttached={isAttached}
                            onAskAI={() => {
                              const creative = adCreatives[currentAdIndex];
                              if (
                                creative &&
                                sectionId &&
                                creative.creative_id &&
                                creative.ad_id
                              ) {
                                attachCreativeAndOpenChat(
                                  {
                                    creative_id: creative.creative_id,
                                    ad_id: creative.ad_id,
                                    section_id: sectionId,
                                    thumbnail_url:
                                      creative.thumbnail_url ||
                                      creative.images?.[0]?.permalink_url ||
                                      creative.videos?.[0]?.thumbnail_url,
                                    type: creative.type,
                                    ad_name: creative.ad_name,
                                    media_url:
                                      creative.images?.[0]?.permalink_url ||
                                      creative.videos?.[0]?.permalink_url ||
                                      "",
                                  },
                                  groupByKey,
                                  sectionMetricLabels,
                                );
                              }
                            }}
                            onDetach={() => {
                              const creative = adCreatives[currentAdIndex];
                              if (creative && sectionId && creative.creative_id) {
                                detachAndMaybeCloseChat(creative.creative_id, sectionId);
                              }
                            }}
                          />
                        ),
                        position: "bottom-left" as const,
                        visibility,
                      },
                    ];
                  })()
                : []),
              {
                component: (
                  <OptionsAction
                    url={
                      adCreatives[currentAdIndex]?.videos?.[0]
                        ? adCreatives[
                            currentAdIndex
                          ]?.videos?.[0]?.permalink_url?.toLowerCase?.() === "n/a"
                          ? null
                          : adCreatives[currentAdIndex]?.videos?.[0]?.permalink_url
                        : (
                              adCreatives[currentAdIndex]?.images?.[0]
                                ?.uncompressed_url ||
                              adCreatives[currentAdIndex]?.images?.[0]?.permalink_url
                            )?.toLowerCase?.() === "n/a"
                          ? null
                          : adCreatives[currentAdIndex]?.images?.[0]?.uncompressed_url ||
                            adCreatives[currentAdIndex]?.images?.[0]?.permalink_url
                    }
                    ad_id={adCreatives[currentAdIndex]?.ad_id || ""}
                    accountId={accountId || ""}
                    accountName={adCreatives[currentAdIndex]?.account_name || ""}
                    fbPostUrl={
                      adCreatives[currentAdIndex]?.fb_post_url?.toLowerCase() === "n/a"
                        ? null
                        : adCreatives[currentAdIndex]?.fb_post_url
                    }
                    igPostUrl={
                      adCreatives[currentAdIndex]?.ig_post_url?.toLowerCase() === "n/a"
                        ? null
                        : adCreatives[currentAdIndex]?.ig_post_url
                    }
                    tiktokPostUrl={
                      adCreatives[currentAdIndex]?.tiktok_post_url?.toLowerCase() ===
                      "n/a"
                        ? null
                        : adCreatives[currentAdIndex]?.tiktok_post_url
                    }
                    type={adCreatives[currentAdIndex]?.videos?.[0] ? "video" : "image"}
                    labelManagementProps={
                      groupType === "text_ai_label"
                        ? {
                            uniqueLabels: labelData?.unique_labels || [],
                            currentLabelItems: getCurrentLabelItems(
                              adCreatives[currentAdIndex]?.ad_id || "",
                            ),
                            onLabelSelect: (labelItem) =>
                              void handleLabelsUpdate(
                                adCreatives[currentAdIndex]?.ad_id || "",
                                [labelItem],
                              ),
                            onLabelsSave: (labelItems) =>
                              void handleLabelsUpdate(
                                adCreatives[currentAdIndex]?.ad_id || "",
                                labelItems,
                              ),
                            labelView: labelView,
                            creativeKind: adCreatives[currentAdIndex]?.type as
                              | "image"
                              | "video"
                              | "mixed"
                              | undefined,
                          }
                        : null
                    }
                  />
                ),
                position: "top-right",
                visibility: "hover",
              },
            ],
          },
        }}
      />

      {formattedMetrics.length > 0 && (
        <CardMetrics isAdView={isAdView} metrics={formattedMetrics} />
      )}
    </div>
  );
};

export default CreativeCard;
